import { Button, Popover, Tag, Image } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import * as React from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

interface Props {
    handleClick: (key: string, label: string) => void;
    suggestions: {
        id: number;
        name: string;
        thumbnail: string;
        version: string;
    }[];
    suggested_text?: string | null;
    previewImage?: string | null;
    suggestionType: 'Material' | 'Finish' | 'Coating';
    for_staff: boolean;
}

const SuggestionField: React.FC<Props> = props => {
    const maxVersion = Math.max(...props.suggestions.map(item => parseFloat(item.version)));
    const latestSuggestions = props.suggestions.filter(item => parseFloat(item.version) === maxVersion);
    const previewImage = latestSuggestions.find(item => item.thumbnail)?.thumbnail || props.previewImage;

    const suggestionLabel = 'Suggested:';

    // Dynamic extracted text based on suggestion type
    const extractedTextPrefix =
        props.suggestionType === 'Material'
            ? 'Material Extracted:'
            : props.suggestionType === 'Finish'
            ? 'Finish Extracted:'
            : 'Coating Extracted:';

    const extractedText = props.suggested_text ? (
        <div className="d-flex align-items-center justify-content-between">
            <span>
                <strong>{extractedTextPrefix}</strong> {props.suggested_text}
            </span>
            {previewImage ? (
                <Popover
                    placement="top"
                    title={`${props.suggestionType} source`}
                    content={
                        <div
                            className="border suggestion-source-preview"
                            style={{ maxWidth: 500, textAlign: 'center' }}
                        >
                            <Image preview={false} style={{ maxWidth: '90%', margin: 'auto' }} src={previewImage} />
                        </div>
                    }
                >
                    <Button
                        aria-label={`Preview extracted ${props.suggestionType.toLowerCase()} text`}
                        icon={<EyeOutlined />}
                        size="small"
                        type="text"
                    />
                </Popover>
            ) : null}
        </div>
    ) : (
        ''
    );

    return (
        <div className="suggestion-field d-flex flex-column rounded-sm mb-1" style={{ padding: '4px 6px' }}>
            {extractedText && props.for_staff && (
                <div
                    className="suggestion-field-extracted"
                    style={{
                        padding: '4px 6px',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        fontSize: 13,
                    }}
                >
                    {extractedText}
                </div>
            )}

            <div className="d-flex flex-row align-items-center">
                <span
                    style={{ width: 72, flexShrink: 0, fontSize: 13 }}
                    className="suggestion-field-label font-weight-bold"
                >
                    {suggestionLabel}
                </span>
                <Scrollbars style={{ width: '100%' }} autoHide autoHeight autoHeightMin={24} autoHeightMax={48}>
                    {latestSuggestions.length ? (
                        latestSuggestions.map((item, index) => (
                            <div
                                key={index}
                                className={'suggestion'}
                                style={{
                                    display: 'inline-block',
                                    cursor: 'pointer',
                                }}
                            >
                                <Tag
                                    className="border border-dashed border-green"
                                    style={{
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        margin: '0 4px 0 0',
                                        lineHeight: '20px',
                                    }}
                                    color={item.version === '1.0' ? 'green' : 'blue'}
                                    onClick={() => {
                                        props.handleClick(item.id.toString(), item.name);
                                    }}
                                >
                                    {item.name}
                                </Tag>
                            </div>
                        ))
                    ) : (
                        <span className="text-muted" style={{ fontSize: 13 }}>
                            No suggestions
                        </span>
                    )}
                </Scrollbars>
            </div>
        </div>
    );
};

export default SuggestionField;
